import React from 'react';
import { CountryCode, MedicationProductCodeKey } from '@dayetopia/types';
import styled from 'styled-components';
import { getMedications } from '@components/dashboard/data/MedicationsData';
import { getTreatmentById } from '@components/dashboard/data/TreatmentsData';
import LazyLoadImage from '@components/shared/LazyLoadImage';
import {
  CART_ITEM_BALM_IMG,
  CART_ITEM_BLMPIN_IMG,
  CART_ITEM_CANDLE_IMG,
  CART_ITEM_CONSULTATION_IMG,
  CART_ITEM_FESTIVE_BUNDLE_IMG,
  CART_ITEM_GIFTCARD_IMG,
  CART_ITEM_MASKBLUE_IMG,
  CART_ITEM_MASKDARK_IMG,
  CART_ITEM_MASKLIGHT_IMG,
  CART_ITEM_MASKPINK_IMG,
  CART_ITEM_PADS_IMG,
  CART_ITEM_PADS_LINER,
  CART_ITEM_PADS_REGULAR,
  CART_ITEM_PADS_STACK_IMG,
  CART_ITEM_PADS_SUPER,
  CART_ITEM_PADS_SUPER_PLUS,
  CART_ITEM_PPC_IMG,
  CART_ITEM_PROVIOTICS_IMG,
  CART_ITEM_SAMPLER_IMG,
  CART_ITEM_TAMPONBOX_IMG,
  CART_ITEM_TAMPONBUNDLECBD_IMG,
  CART_ITEM_TAMPONBUNDLEHEAVY_IMG,
  CART_ITEM_TAMPONBUNDLE_IMG,
  CART_ITEM_TAMPONPADS_IMG,
  CART_ITEM_TAMPONPOUCH_IMG,
  CART_ITEM_TAMPONSNUDE_IMG,
  CART_ITEM_TAMPONS_IMG,
  CART_ITEM_TAMPONTIN_IMG,
  CART_ITEM_VAGINAMATTERS_IMG,
  CART_ITEM_VALENTINESBOX_IMG,
  CART_ITEM_VALETINES_IMG,
  CART_ITEM_VMS_IMG
} from '@constants';
import { ProductCode, ProductCodeKey } from '@contracts';
import { getProductTitle } from '@data/products';
import { useCountry } from '@hooks/useCountry';
import { isPadsStackBox } from '@utils/pads';

interface Props {
  id?: string | null;
  style?: React.CSSProperties | undefined;
  imageVersion?: number | string;
}

const vmsCartItems = ['proviotics_vaginal_health', 'vms_essential', 'sti_screening', 'vms_advanced', 'hpv_screening', 'hpv_advanced', 'vms_premium'];

export const getProductImage = (imageId: ProductCodeKey, country: CountryCode, imageVersion: string | number = 1) => {
  switch (imageId) {
    case ProductCode.TamponBox:
      if (imageVersion === 2) {
        return CART_ITEM_TAMPONPADS_IMG;
      }
      return CART_ITEM_TAMPONBOX_IMG;
    case ProductCode.Proviotics:
    case ProductCode.ProvioticsMultiMonth:
      return CART_ITEM_PROVIOTICS_IMG;
    case ProductCode.Sampler:
      return CART_ITEM_SAMPLER_IMG;
    case ProductCode.MaskPink:
      return CART_ITEM_MASKPINK_IMG;
    case ProductCode.MaskBlue:
      return CART_ITEM_MASKBLUE_IMG;
    case ProductCode.MaskLight:
      return CART_ITEM_MASKLIGHT_IMG;
    case ProductCode.MaskDark:
      return CART_ITEM_MASKDARK_IMG;
    case ProductCode.VaginaMattersBook:
      return CART_ITEM_VAGINAMATTERS_IMG;
    case ProductCode.GiftCard:
      return CART_ITEM_GIFTCARD_IMG;
    case ProductCode.BlmPin:
      return CART_ITEM_BLMPIN_IMG;
    case ProductCode.ValentinesSampler:
      return CART_ITEM_VALETINES_IMG;
    case ProductCode.ValentinesBox:
      return CART_ITEM_VALENTINESBOX_IMG;
    case ProductCode.TamponPouch:
      return CART_ITEM_TAMPONPOUCH_IMG;
    case ProductCode.TamponTin:
      return CART_ITEM_TAMPONTIN_IMG;
    case ProductCode.ScreeningKit:
      return CART_ITEM_VMS_IMG;
    case ProductCode.VMSEssential:
      return CART_ITEM_VMS_IMG;
    case ProductCode.VMSEssentialReplacement:
      return CART_ITEM_VMS_IMG;
    case ProductCode.PadsBox24L:
      return CART_ITEM_PADS_LINER;
    case ProductCode.PadsBox12R:
      return CART_ITEM_PADS_REGULAR;
    case ProductCode.PadsBox12S:
      return CART_ITEM_PADS_SUPER;
    case ProductCode.PadsBox10SP:
      return CART_ITEM_PADS_SUPER_PLUS;
    case ProductCode.FestiveBundle:
      return CART_ITEM_FESTIVE_BUNDLE_IMG;
    case ProductCode.VMSMedicationFee: {
      const treatment = getTreatmentById(imageVersion as MedicationProductCodeKey);

      if (treatment) {
        const medications = getMedications({ names: [treatment.medication] });

        if (medications.length === 1) {
          return medications[0].cartImage || medications[0].smallImage;
        }
      }

      return CART_ITEM_VMS_IMG;
    }
    case ProductCode.VMSPrescriptionFee:
      return CART_ITEM_VMS_IMG;
    case ProductCode.RoseCandle:
      return CART_ITEM_CANDLE_IMG;
    case ProductCode.Pads:
      if (isPadsStackBox(country)) {
        return CART_ITEM_PADS_STACK_IMG;
      }

      return CART_ITEM_PADS_IMG;
    case ProductCode.PPCTamponBox:
      return CART_ITEM_TAMPONBOX_IMG;
    case ProductCode.PPCStandard:
    case ProductCode.PPCAdvanced:
      return CART_ITEM_PPC_IMG;
    case ProductCode.PPCPremium:
      return CART_ITEM_VMS_IMG;
    default:
      if (vmsCartItems.some((item) => imageId.includes(item))) {
        return CART_ITEM_VMS_IMG;
      }
      if (imageId.includes('consultation') || imageId.includes('fee')) {
        return CART_ITEM_CONSULTATION_IMG;
      }
      if (imageId.includes('tampon_box_bundle_cbd')) {
        return CART_ITEM_TAMPONBUNDLECBD_IMG;
      }
      if (imageId.includes('tampon_box_bundle_heavy_flow')) {
        return CART_ITEM_TAMPONBUNDLEHEAVY_IMG;
      }
      if (imageId.includes('tampon_box_bundle')) {
        return CART_ITEM_TAMPONBUNDLE_IMG;
      }
      if (imageId.includes('cs') || imageId.includes('cr')) {
        return CART_ITEM_TAMPONS_IMG;
      }
      if (imageId.includes('ns') || imageId.includes('nr')) {
        return CART_ITEM_TAMPONSNUDE_IMG;
      }
      if (imageId.includes('tampon_box')) {
        return CART_ITEM_TAMPONBOX_IMG;
      }
      if (imageId.includes('pads_box')) {
        return CART_ITEM_PADS_IMG;
      }
      return '';
  }
};

const ProductImage: React.FC<Props> = ({ id, style, imageVersion = 1 }) => {
  const { country } = useCountry();
  const image = getProductImage(id as ProductCodeKey, country, imageVersion);
  const productTitle = getProductTitle(id as ProductCodeKey);

  return <CartItemImageWrap style={style}>{image && <ImgStyled src={image} alt={productTitle} />}</CartItemImageWrap>;
};

export default ProductImage;

const CartItemImageWrap = styled.div`
  width: 40px;
  min-width: 40px;
  height: 40px;
  overflow: hidden;
  border-radius: 50%;
  margin-right: 15px;
`;

const ImgStyled = styled(LazyLoadImage)`
  display: block;
  width: 100%;
  height: 100%;
`;
